/// <reference types="vite/client" />
/**
 * Todas as rotas são CARREGADAS SOB DEMANDA (`() => import(...)`).
 *
 * Antes eram 42 imports estáticos, e o resultado era um chunk de entrada único
 * com o app do operador e o painel de admin dentro — quem só abre o chat
 * baixava e interpretava as 33 páginas do admin, e vice-versa. O chunk passou
 * do teto de 5 MiB do `injectManifest`, que o vite-plugin-pwa trata como ERRO,
 * e o build começou a falhar.
 *
 * O service worker segue precacheando tudo, então isto NÃO reduz o que o
 * usuário baixa no primeiro load: reduz o que ele INTERPRETA no boot, e tira o
 * chunk de entrada do teto. Navegar não vai à rede — o chunk já está no cache.
 *
 * ⚠️ Ao adicionar rota, use `() => import(...)`, não import estático. Um só
 * basta para arrastar a dependência inteira de volta ao chunk de entrada.
 */
import { createRouter, createWebHistory, NavigationGuardNext, RouteLocationNormalizedGeneric, RouteLocationNormalizedLoadedGeneric, RouteRecordRaw } from 'vue-router'
import { useAuthStoreAdmin } from '@/stores/admin/authAdmin'
import { useAuthStoreUser } from '@/stores/app/authUser'
import { OverrideConsole } from '@/utils/override-console'
import { branding } from '@/config/branding';
// import { useOnboardingStore } from '@/stores/app/onboardingStore';

OverrideConsole()

const routes: Array<RouteRecordRaw> = [

  // AMBIENTE ADMINISTRADOR
  {
    path: '/admin',
    name: 'LoginPage',
    component: () => import('@/views/admin/LoginPage.vue')
  },
  {
    path: '/admin-contas',
    name: 'ContasPage',
    component: () => import('@/views/admin/ContasPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-usuarios',
    name: 'UsuariosPage',
    component: () => import('@/views/admin/UsuariosPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-administradores',
    name: 'AdministradoresPage',
    component: () => import('@/views/admin/AdministradoresPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-ambientes',
    name: 'AmbientesPage',
    component: () => import('@/views/admin/AmbientesPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-servidores',
    name: 'ServidoresPage',
    component: () => import('@/views/admin/ServidoresPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-auditoria',
    name: 'AuditoriaPage',
    component: () => import('@/views/admin/AuditoriaPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-user-auditoria',
    name: 'UserAuditoriaPage',
    component: () => import('@/views/admin/UserAuditoriaPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-novidades',
    name: 'NovidadesPage',
    component: () => import('@/views/admin/NovidadesPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-faqs',
    name: 'FaqsPage',
    component: () => import('@/views/admin/FaqsPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-faqs/tags',
    name: 'FaqTagsPage',
    component: () => import('@/views/admin/FaqTagsPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-faqs/nova',
    name: 'FaqNovaPage',
    component: () => import('@/views/admin/FaqEditorPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-faqs/:id/editar',
    name: 'FaqEditarPage',
    component: () => import('@/views/admin/FaqEditorPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-faqs/:slug',
    name: 'FaqPage',
    component: () => import('@/views/admin/FaqPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-inboxes',
    name: 'InboxesPage',
    component: () => import('@/views/admin/InboxesPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-webhooks',
    name: 'WebhooksPage',
    component: () => import('@/views/admin/WebhooksPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-api-keys',
    name: 'ApiKeysPage',
    component: () => import('@/views/admin/ApiKeysPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-analise-risco',
    name: 'AnaliseRiscoPage',
    component: () => import('@/views/admin/AnaliseRiscoPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-logs-erro',
    name: 'LogsErroPage',
    component: () => import('@/views/admin/LogsErroPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-logs-canais',
    name: 'LogsCanaisPage',
    component: () => import('@/views/admin/LogsCanaisPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-logs-conexao',
    name: 'LogsConexaoPage',
    component: () => import('@/views/admin/LogsConexaoPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-mensagens-diarias',
    name: 'MensagensDiariasPage',
    component: () => import('@/views/admin/MensagensDiariasPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-relatorio-uso',
    name: 'RelatorioUsoPage',
    component: () => import('@/views/admin/RelatorioUsoPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-meta-templates',
    name: 'MetaTemplatesPage',
    component: () => import('@/views/admin/MetaTemplatesPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-meta-template-eventos',
    name: 'MetaTemplateEventosPage',
    component: () => import('@/views/admin/MetaTemplateEventosPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-meta-webhooks',
    name: 'MetaWebhooksPage',
    component: () => import('@/views/admin/MetaWebhooksPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-meta-onboarding',
    name: 'MetaOnboardingPage',
    component: () => import('@/views/admin/MetaOnboardingPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  // Onboarding WhatsApp no modelo de contas novo da Meta (WAME).
  {
    path: '/admin-whatsapp-wame',
    name: 'WhatsappWamePage',
    component: () => import('@/views/admin/WhatsappWamePage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  // Trilha de Bots (mesmas rotas nas três branches).
  {
    path: '/admin-bot-trilha',
    name: 'BotTrilhaPage',
    component: () => import('@/views/admin/BotTrilhaPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    // Antes de `/:id` só por legibilidade — o vue-router já prefere o segmento estático.
    path: '/admin-bot-trilha/comparar',
    name: 'BotTrilhaComparacaoPage',
    component: () => import('@/views/admin/BotTrilhaComparacaoPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-bot-trilha/:id',
    name: 'BotTrilhaExecucaoPage',
    component: () => import('@/views/admin/BotTrilhaExecucaoPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-contatos-trilha',
    name: 'ContatosTrilhaPage',
    component: () => import('@/views/admin/ContatosTrilhaPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-meta-graph-console',
    name: 'MetaGraphConsolePage',
    component: () => import('@/views/admin/MetaGraphConsolePage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-meta-migracao-roteiro',
    name: 'MetaMigracaoRoteiroPage',
    component: () => import('@/views/admin/MetaMigracaoRoteiroPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-integracao-remsono',
    name: 'RemsonoPage',
    component: () => import('@/views/admin/RemsonoPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    // Caminho preservado: a página nativa SUBSTITUI o antigo embed do painel do bot-ia, e trocar
    // a rota quebraria os links já salvos por quem usava a tela.
    path: '/admin-relatorios-bot-ia',
    name: 'RelatorioIaAdminPage',
    component: () => import('@/views/admin/RelatorioIaAdminPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    // O embed do painel do bot-ia, de volta — mas só para as instâncias que NÃO são WHIZV2.
    // A tela acima é nativa e fala em Atendimento, então não alcança essas instâncias; sem esta
    // rota elas ficaram sem nenhum lugar onde ver custo.
    path: '/admin-relatorios-bot-ia-sem-whiz',
    name: 'RelatoriosBotIaSemWhizPage',
    component: () => import('@/views/admin/RelatoriosBotIaSemWhizPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-bot-ia-app',
    name: 'BotIaAppPage',
    component: () => import('@/views/admin/BotIaAppPage.vue'),
    // Embed do app completo do bot-ia: restrito a administradores root.
    meta: { requiresAuthAdmin: true, requiresRootAdmin: true }
  },
  {
    path: '/admin-bot-ia-forms',
    name: 'BotIaFormsPage',
    component: () => import('@/views/admin/BotIaFormsPage.vue'),
    // Formulários embarcados de Instância/Agente: os escopos de token são plataforma-wide
    // (alcançam qualquer recurso), então valem a mesma restrição do app completo.
    meta: { requiresAuthAdmin: true, requiresRootAdmin: true }
  },
  {
    path: '/admin-banco-conhecimento-modulos',
    name: 'BancoConhecimentoModulosPage',
    component: () => import('@/views/admin/BancoConhecimentoModulosPage.vue'),
    meta: { requiresAuthAdmin: true }
  },
  {
    path: '/admin-banco-conhecimento-videos',
    name: 'BancoConhecimentoVideosPage',
    component: () => import('@/views/admin/BancoConhecimentoVideosPage.vue'),
    meta: { requiresAuthAdmin: true }
  },

  // AMBIENTE USUARIO FINAL
  {
    path: '/',
    name: 'LoginPageUser',
    component: () => import('@/views/app/LoginPageUser.vue')
  },

  {
    path: '/app',
    name: 'PainelPage',
    component: () => import('@/views/app/PainelPage.vue'),
    meta: { requiresAuthUser: true }
  },

  {
    path: '/verificar-2fa',
    name: 'Verificar2FAPage',
    component: () => import('@/views/app/Verificar2FAPage.vue')
  },
  {
    path: '/esqueceu-senha',
    name: 'EsqueceuSenhaPage',
    component: () => import('@/views/app/EsqueceuSenhaPage.vue')
  },
  {
    path: '/conta-congelada',
    name: 'ContaCongeladaPage',
    component: () => import('@/views/app/ContaCongeladaPage.vue')
  },
  {
    path: '/redefinir-senha',
    name: 'RedefinirSenhaPage',
    component: () => import('@/views/app/RedefinirSenhaPage.vue')
  },
  {
    path: '/embed',
    name: 'ChatEmbed',
    component: () => import('../views/app/ChatEmbedPage.vue')
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('@/components/onboarding/OnboardingWizard.vue'),
    meta: { requiresAuthUser: true }
  },

  // Rota pública de retorno do popup de Embedded Signup (auto-onboarding do
  // usuário). Sem guarda: o popup apenas repassa o postMessage e se fecha.
  {
    path: '/whatsapp-onboarding/callback',
    name: 'WhatsappOnboardingCallback',
    component: () => import('@/views/app/WhatsappOnboardingCallback.vue')
  },

  // Rota pública de retorno do popup OAuth Meta (reconexão pelo cliente/operador).
  // Sem guarda: o operador não tem auth de admin, então não pode cair em /admin-inboxes.
  {
    path: '/meta-oauth/callback',
    name: 'MetaOauthCallback',
    component: () => import('@/views/app/MetaOauthCallback.vue')
  },

]

const router = createRouter({
  // eslint-disable-next-line
  history: createWebHistory(import.meta.env.BASE_URL ?? '/'),
  routes
})

router.beforeEach(async (to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) => {
  if (to.path.startsWith('/admin')) {
    document.title = branding.titles.admin;
  } else {
    document.title = branding.titles.user;
  }

  // AMBIENTE USUÁRIO
  if (to.meta.requiresAuthUser) {
    const authStoreUser = useAuthStoreUser()
    await authStoreUser.init();

    if (!authStoreUser.isLoggedIn) {
      return next('/')
    }

    // Redireciona para onboarding se a conta ainda não concluiu o setup
    const onboardingStep = authStoreUser.currentConta?.onboardingStep;
    if (onboardingStep != null && to.path !== '/onboarding') {
      return next('/onboarding');
    }
  }
  
  // AMBIENTE ADMIN
  else if (to.meta.requiresAuthAdmin) {
    const authStoreAdmin = useAuthStoreAdmin();
    await authStoreAdmin.init();
    if (!authStoreAdmin.isLoggedIn)
      return next('/admin')
    // Rotas root-only (ex.: embed do app completo do bot-ia).
    if (to.meta.requiresRootAdmin && !authStoreAdmin.adminInfo?.root)
      return next('/admin-contas')
  }

  next()
})

export default router